import type { OpaqueCursorPagination } from './authorization.types';
import type { JsonObject } from './common.types';
import type { OpaqueCursorSearchRequest } from './omnisearch.types';
import type { Role } from '@pillar-portal/database/dist/enums';
import type {
    CredentialType,
    IpxCredentialDefinitionKind,
} from '@pillar-portal/database/dist/enums';
import type {
    CredentialOwnerScope,
    IpxCredentialFieldConfigMap,
    SupportedCredentialSystemId,
    StoredIpxCredentialFieldConfigMap,
} from './secret.types';

export const TransactionLogOperations = [
    'INSERT',
    'UPDATE',
    'DELETE',
    'LOGIN',
    'MFA_SUCCESS',
    'ROLE_ASSIGNMENT_ACTIVATED',
    'LOGOUT_SUCCESS',
    'SESSION_EXPIRED',
    'IMPERSONATION_STARTED',
    'IMPERSONATION_ENDED',
    'CREDENTIAL_HISTORICAL_REVEAL_SUCCEEDED',
    'CREDENTIAL_HISTORICAL_REVEAL_FAILED',
    'EMAIL_AUTHENTICATION_ATTEMPT_LIMIT_RESET_SUCCEEDED',
    'EMAIL_AUTHENTICATION_ATTEMPT_LIMIT_RESET_FAILED',
] as const;

export type TransactionLogOperation = (typeof TransactionLogOperations)[number];

export const TransactionLogTableNames = [
    'users',
    'organizations',
    'merchant_customer_records',
    'role_assignments',
    'totp_backup_codes',
    'ipw_customer_links',
    'payment_method_tokens',
    'stored_payment_methods',
    'credit_card_stored_payment_details',
    'debit_card_stored_payment_details',
    'ach_stored_payment_details',
    'payment_requests',
    'payment_request_delivery_events',
    'payment_request_documents',
    'document_templates',
    'payment_transactions',
    'payment_transaction_lifecycle_events',
    'credentials',
    'credential_rotation_attempts',
    'permissions_bundles',
    'permissions_policy_grants',
    'legal_documents',
    'legal_document_assignments',
    'legal_document_content_snapshots',
    'user_legal_document_acceptances',
    'organization_legal_document_acceptances',
    'processors',
    'merchant_accounts',
    'logical_terminals',
    'terminal_devices',
    'allowed_website_templates',
    'organization_allowed_websites',
    'ipx_rbs_workflow_instances',
] as const;

export type TransactionLogTableName = (typeof TransactionLogTableNames)[number];

export interface TransactionLogTableRowSnapshot extends JsonObject {}

export interface TransactionLogRowDetails extends JsonObject {}

export interface TransactionLogCredentialProtectedPayloadV1 {
    protected_payload_version: 1;
    protected_payload_kind: 'CREDENTIAL_DML_SNAPSHOT';
    ciphertext_encoding: 'BASE64';
    ciphertext_base64: string;
}

export interface TransactionLogCredentialDefinitionEvidenceV2 {
    readonly uuid: string;
    readonly ipx_system_id: SupportedCredentialSystemId;
    readonly credential_definition_key: string;
    readonly credential_definition_kind: IpxCredentialDefinitionKind;
    readonly credential_type: CredentialType;
    readonly credential_owner_scope: CredentialOwnerScope;
    readonly credential_field_config: StoredIpxCredentialFieldConfigMap;
}

export interface TransactionLogCredentialDefinitionEvidence extends Omit<
    TransactionLogCredentialDefinitionEvidenceV2,
    'credential_field_config'
> {
    readonly credential_field_config: IpxCredentialFieldConfigMap;
}

export interface TransactionLogCredentialProtectedPayloadV2 {
    readonly protected_payload_version: 2;
    readonly protected_payload_kind: 'CREDENTIAL_DML_SNAPSHOT';
    readonly ciphertext_encoding: 'BASE64';
    readonly ciphertext_base64: string;
    readonly credential_definition: TransactionLogCredentialDefinitionEvidenceV2;
}

export type StoredTransactionLogCredentialProtectedPayload =
    | TransactionLogCredentialProtectedPayloadV1
    | TransactionLogCredentialProtectedPayloadV2;

export interface TransactionLogCredentialProtectedPayload {
    readonly ciphertext_encoding: 'BASE64';
    readonly ciphertext_base64: string;
    readonly credential_definition: TransactionLogCredentialDefinitionEvidence | null;
}

export const CredentialHistoricalRevealFailureReasons = [
    'ENTRY_NOT_FOUND',
    'NOT_CREDENTIAL_DML',
    'PERSONAL_CREDENTIAL',
    'PROTECTED_EVIDENCE_UNAVAILABLE',
    'HISTORICAL_DEFINITION_UNAVAILABLE',
    'FIELD_UNAVAILABLE',
    'HISTORICAL_KEY_UNAVAILABLE',
    'DECRYPTION_FAILED',
    'AUDIT_STORAGE_FAILED',
] as const;
export type CredentialHistoricalRevealFailureReason =
    (typeof CredentialHistoricalRevealFailureReasons)[number];

interface CredentialHistoricalRevealEventDetailsV1Common {
    readonly event_details_version: 1;
    readonly transaction_log_ref: string | null;
    readonly field_name: string | null;
}

export interface CredentialHistoricalRevealSucceededEventDetailsV1 extends CredentialHistoricalRevealEventDetailsV1Common {
    readonly outcome: 'SUCCEEDED';
}

export interface CredentialHistoricalRevealFailedEventDetailsV1 extends CredentialHistoricalRevealEventDetailsV1Common {
    readonly outcome: 'FAILED';
    readonly failure_reason: CredentialHistoricalRevealFailureReason;
}

export type CredentialHistoricalRevealEventDetailsV1 =
    | CredentialHistoricalRevealSucceededEventDetailsV1
    | CredentialHistoricalRevealFailedEventDetailsV1;
export type CredentialHistoricalRevealEventDetails = CredentialHistoricalRevealEventDetailsV1;

export const EmailAuthenticationAttemptLimitResetFailureCodes = [
    'NOT_AUTHORIZED',
    'RESET_FAILED',
] as const;
export type EmailAuthenticationAttemptLimitResetFailureCode =
    (typeof EmailAuthenticationAttemptLimitResetFailureCodes)[number];

interface EmailAuthenticationAttemptLimitResetEventDetailsV1Common {
    readonly event_details_version: 1;
    readonly target_user_uuid: string;
}

export interface EmailAuthenticationAttemptLimitResetSucceededEventDetailsV1 extends EmailAuthenticationAttemptLimitResetEventDetailsV1Common {
    readonly outcome: 'SUCCEEDED';
}

export interface EmailAuthenticationAttemptLimitResetFailedEventDetailsV1 extends EmailAuthenticationAttemptLimitResetEventDetailsV1Common {
    readonly outcome: 'FAILED';
    readonly failure_code: EmailAuthenticationAttemptLimitResetFailureCode;
}

// Discriminator: outcome
export type EmailAuthenticationAttemptLimitResetEventDetailsV1 =
    | EmailAuthenticationAttemptLimitResetSucceededEventDetailsV1
    | EmailAuthenticationAttemptLimitResetFailedEventDetailsV1;
export type EmailAuthenticationAttemptLimitResetEventDetails =
    EmailAuthenticationAttemptLimitResetEventDetailsV1;

export interface RevealHistoricalCredentialFieldRequest {
    readonly transaction_log_ref: string;
    readonly field_name: string;
}

export interface RevealHistoricalCredentialFieldResponse {
    readonly credential_historical_reveal_version: 1;
    readonly transaction_log_ref: string;
    readonly field_name: string;
    readonly field_value: string;
}

export interface CredentialHistoricalRevealFieldDescriptor {
    readonly field_name: string;
    readonly label: string;
}

export type CredentialHistoricalRevealAvailability =
    | { readonly kind: 'NOT_APPLICABLE' }
    | {
          readonly kind: 'AVAILABLE';
          readonly historical_ts: string;
          readonly credential_definition_uuid: string;
          readonly fields: readonly CredentialHistoricalRevealFieldDescriptor[];
      }
    | {
          readonly kind: 'UNAVAILABLE';
          readonly reason:
              | 'LEGACY_PROTECTED_EVIDENCE'
              | 'PROTECTED_EVIDENCE_UNAVAILABLE'
              | 'HISTORICAL_DEFINITION_UNAVAILABLE';
      };

export interface ForensicUserReference {
    user_uuid: string;
    primary_email: string;
}

export interface ForensicOrganizationReference {
    organization_uuid: string;
    name: string;
}

export interface ForensicRoleAssignmentReference {
    role_assignment_uuid: string;
    role: Role;
    user: ForensicUserReference;
    organization: ForensicOrganizationReference | null;
}

export interface TransactionLogListItemCommon {
    transaction_log_ref: string;
    ts: string;
    actor: ForensicUserReference;
    session_ref: string | null;
    effective_role_assignment: ForensicRoleAssignmentReference | null;
    ip_address: string | null;
    user_agent: string | null;
    credential_historical_reveal: CredentialHistoricalRevealAvailability;
}

export interface TransactionLogDmlListItemCommon extends TransactionLogListItemCommon {
    table_name: TransactionLogTableName;
    row_details: TransactionLogRowDetails;
    effective_role_assignment: ForensicRoleAssignmentReference;
}

export interface TransactionLogInsertListItem extends TransactionLogDmlListItemCommon {
    operation: 'INSERT';
}

export interface TransactionLogUpdateListItem extends TransactionLogDmlListItemCommon {
    operation: 'UPDATE';
}

export interface TransactionLogDeleteListItem extends TransactionLogDmlListItemCommon {
    operation: 'DELETE';
}

export interface TransactionLogLoginListItem extends TransactionLogListItemCommon {
    operation: 'LOGIN';
}

export interface TransactionLogMfaSuccessListItem extends TransactionLogListItemCommon {
    operation: 'MFA_SUCCESS';
}

export interface TransactionLogRoleAssignmentActivatedListItem extends TransactionLogListItemCommon {
    operation: 'ROLE_ASSIGNMENT_ACTIVATED';
    effective_role_assignment: ForensicRoleAssignmentReference;
}

export interface TransactionLogLogoutSuccessListItem extends TransactionLogListItemCommon {
    operation: 'LOGOUT_SUCCESS';
}

export interface TransactionLogSessionExpiredListItem extends TransactionLogListItemCommon {
    operation: 'SESSION_EXPIRED';
}

export interface TransactionLogImpersonationStartedListItem extends TransactionLogListItemCommon {
    operation: 'IMPERSONATION_STARTED';
    effective_role_assignment: ForensicRoleAssignmentReference;
}

export interface TransactionLogImpersonationEndedListItem extends TransactionLogListItemCommon {
    operation: 'IMPERSONATION_ENDED';
    effective_role_assignment: ForensicRoleAssignmentReference;
}

export interface TransactionLogCredentialHistoricalRevealSucceededListItem extends TransactionLogListItemCommon {
    operation: 'CREDENTIAL_HISTORICAL_REVEAL_SUCCEEDED';
    effective_role_assignment: ForensicRoleAssignmentReference;
}

export interface TransactionLogCredentialHistoricalRevealFailedListItem extends TransactionLogListItemCommon {
    operation: 'CREDENTIAL_HISTORICAL_REVEAL_FAILED';
    effective_role_assignment: ForensicRoleAssignmentReference;
}

export interface TransactionLogEmailAuthenticationAttemptLimitResetSucceededListItem extends TransactionLogListItemCommon {
    operation: 'EMAIL_AUTHENTICATION_ATTEMPT_LIMIT_RESET_SUCCEEDED';
    effective_role_assignment: ForensicRoleAssignmentReference;
    event_details: EmailAuthenticationAttemptLimitResetSucceededEventDetailsV1;
}

export interface TransactionLogEmailAuthenticationAttemptLimitResetFailedListItem extends TransactionLogListItemCommon {
    operation: 'EMAIL_AUTHENTICATION_ATTEMPT_LIMIT_RESET_FAILED';
    effective_role_assignment: ForensicRoleAssignmentReference;
    event_details: EmailAuthenticationAttemptLimitResetFailedEventDetailsV1;
}

export type TransactionLogListItem =
    | TransactionLogInsertListItem
    | TransactionLogUpdateListItem
    | TransactionLogDeleteListItem
    | TransactionLogLoginListItem
    | TransactionLogMfaSuccessListItem
    | TransactionLogRoleAssignmentActivatedListItem
    | TransactionLogLogoutSuccessListItem
    | TransactionLogSessionExpiredListItem
    | TransactionLogImpersonationStartedListItem
    | TransactionLogImpersonationEndedListItem
    | TransactionLogCredentialHistoricalRevealSucceededListItem
    | TransactionLogCredentialHistoricalRevealFailedListItem
    | TransactionLogEmailAuthenticationAttemptLimitResetSucceededListItem
    | TransactionLogEmailAuthenticationAttemptLimitResetFailedListItem;

export const TransactionLogSortFields = [
    'transaction_log_ref',
    'ts',
    'operation',
    'table_name',
    'row_details',
    'ip_address',
    'user_agent',
] as const;

export type TransactionLogSortField = (typeof TransactionLogSortFields)[number];

export const TransactionLogSortDirections = ['asc', 'desc'] as const;

export type TransactionLogSortDirection = (typeof TransactionLogSortDirections)[number];

export interface TransactionLogSort {
    field: TransactionLogSortField;
    direction: TransactionLogSortDirection;
    json_path?: string[];
}

export interface TransactionLogSearchRequest extends OpaqueCursorSearchRequest {
    sort?: TransactionLogSort;
}

export interface ListTransactionLogPageResponse {
    transaction_log: TransactionLogListItem[];
    pagination: OpaqueCursorPagination;
}

/** A page row this client cannot interpret, kept so the rest of the page still shows. */
export interface UnrecognizedTransactionLogListItem {
    readonly operation: 'UNRECOGNIZED';
    readonly transaction_log_ref: string | null;
    readonly ts: string | null;
}

export type TransactionLogDisplayItem = TransactionLogListItem | UnrecognizedTransactionLogListItem;

export interface ListTransactionLogDisplayPageResponse {
    transaction_log: TransactionLogDisplayItem[];
    pagination: OpaqueCursorPagination;
}

export interface CaptureCredentialAuditBaselineResponse {
    captured_count: number;
}

export interface ForensicOptionSearchRequest {
    terms?: string[];
    limit?: number;
    cursor?: string;
}

export interface ForensicReferenceOption {
    value: string;
    label: string;
}

export interface ForensicSessionOption extends ForensicReferenceOption {
    first_ts: string;
    latest_ts: string;
}

export interface ListForensicReferenceOptionsPageResponse {
    options: ForensicReferenceOption[];
    pagination: OpaqueCursorPagination;
}

export interface ListForensicSessionOptionsPageResponse {
    options: ForensicSessionOption[];
    pagination: OpaqueCursorPagination;
}

export type RecentUserActivityOperation =
    | 'MFA_SUCCESS'
    | 'LOGIN'
    | 'LOGOUT_SUCCESS'
    | 'SESSION_EXPIRED';

export type RecentRoleActivityOperation =
    | 'ROLE_ASSIGNMENT_ACTIVATED'
    | 'IMPERSONATION_STARTED'
    | 'IMPERSONATION_ENDED';

export type RecentActivityUserReference = ForensicUserReference;

export type RecentActivityOrganizationReference = ForensicOrganizationReference;

export type RecentActivityRoleAssignmentReference = ForensicRoleAssignmentReference;

export interface RecentUserActivityListItem {
    activity_ref: string;
    ts: string;
    operation: RecentUserActivityOperation;
    session_ref: string | null;
    user: RecentActivityUserReference;
    effective_role_assignment: RecentActivityRoleAssignmentReference | null;
    ip_address: string | null;
    user_agent: string | null;
}

export interface RecentRoleActivityListItem {
    activity_ref: string;
    ts: string;
    operation: RecentRoleActivityOperation;
    session_ref: string | null;
    actor: RecentActivityUserReference;
    effective_role_assignment: RecentActivityRoleAssignmentReference;
    ip_address: string | null;
    user_agent: string | null;
}

export interface RecentUserActivitySearchRequest {
    user_uuids?: string[];
    limit?: number;
    cursor?: string;
}

export interface RecentRoleActivitySearchRequest {
    user_uuids?: string[];
    organization_uuids?: string[];
    roles?: Role[];
    limit?: number;
    cursor?: string;
}

export interface ListRecentUserActivityPageResponse {
    activity: RecentUserActivityListItem[];
    pagination: OpaqueCursorPagination;
}

export interface ListRecentRoleActivityPageResponse {
    activity: RecentRoleActivityListItem[];
    pagination: OpaqueCursorPagination;
}
